import { FilePdfFilled, FileTextFilled } from "@ant-design/icons"
import { Avatar, Flex, Space, Tag, Typography } from "antd"
import React from "react"

import { IS_PROD } from "../../../../../constants/dev.ts"
import { getDeclineReasonForDisplay } from "../../../../../utils/documents/document-decline-outcome.ts"
import {
  CONTRACT_STATUSES,
  CONTRACT_STATUSES_OBJ
} from "../../../../Contracts/constants/contract-statuses-keys.ts"
import { DocumentVersionRow } from "../../../../DocumentTemplates/components/DocumentVersionRow.tsx"
import { SendForSignatureButton } from "../../../../Esign/components/SendForSignatureButton.tsx"
import {
  isHeldForSigning,
  signsAsVerbatimRecord
} from "../../../../Esign/helpers/signable-record.ts"
import type {
  LeadContract,
  LeadDocumentAttachment
} from "../../../types/lead.documents.types.ts"
import { LeadViewContractItemActions } from "./LeadViewContractItemActions.tsx"

const { Text, Paragraph } = Typography

interface LeadViewContractItemProps {
  contract: LeadContract
  isUpdateAllowed: boolean
  viewOnly: boolean
  onViewContract: (contract: LeadContract) => void
  onShare: (contract: LeadContract) => void
  onDuplicateContract: (contract: LeadContract) => void
  onViewPDF: (attachment: LeadDocumentAttachment) => void
  onSendToReview: (contractId: string, attachmentId: string) => Promise<void>
  onMarkAsSent: (contractId: string) => Promise<void>
}

export function LeadViewContractItem({
  contract,
  isUpdateAllowed,
  viewOnly,
  onViewContract,
  onDuplicateContract,
  onViewPDF,
  onSendToReview,
  onMarkAsSent,
  onShare
}: LeadViewContractItemProps) {
  const statusData = CONTRACT_STATUSES_OBJ?.[contract?.status]
  const isCancelled = contract?.status === CONTRACT_STATUSES.CANCELLED
  const displayReason = getDeclineReasonForDisplay(contract?.decline_reason)
  const latest_attachment =
    contract?.latest_attachment || contract?.attachments?.at(-1)
  const isUploadedFile = latest_attachment?.extra?.uploaded || false

  return (
    <Flex
      vertical
      className={"p-2 rounded-card border border-border"}
      gap={"small"}
    >
      <Flex
        flex={1}
        vertical
        gap={"small"}
        className={"xl:flex-row xl:items-center"}
        justify={"space-between"}
      >
        <Flex align={"center"} wrap gap={"large"} justify={"space-between"}>
          <Flex align={"center"} gap={"middle"}>
            <div
              className={`px-3 flex justify-center align-center aspect-square rounded-lg ${latest_attachment ? "bg-primary/20" : "bg-primary/10"}`}
            >
              {latest_attachment ? (
                <FilePdfFilled className={"text-primary"} />
              ) : (
                <FileTextFilled />
              )}
            </div>

            <div>
              <Flex gap={"small"} align={"center"} className={"mb-0.5"}>
                <Space>
                  <Text strong>{contract?.contract_id} </Text>
                  {!IS_PROD && contract?.is_version_2 && (
                    <Tag className={"text-bold"} color={"red"}>
                      V2
                    </Tag>
                  )}
                  {contract?.proposal_display_id && (
                    <Text type={"secondary"}>
                      ({contract?.proposal_display_id})
                    </Text>
                  )}
                </Space>
                <Tag style={statusData?.style}>{statusData?.label}</Tag>
                {typeof contract?.is_latest_version === "boolean" && (
                  <DocumentVersionRow
                    versionNumber={contract?.version_number}
                    isLatest={contract.is_latest_version}
                  />
                )}
              </Flex>
              <Text type="secondary">Created {contract?.created}</Text>
            </div>
          </Flex>

          <Flex gap={"small"} align={"center"}>
            <Avatar size={28}>{contract.created_by_name?.[0] ?? "U"}</Avatar>
            <Text className={"text-sm"}>{contract.created_by_name}</Text>
          </Flex>
        </Flex>

        <Flex gap="small" wrap className="shrink-0">
          {!viewOnly &&
            signsAsVerbatimRecord(contract) &&
            !isHeldForSigning(contract) && (
              <SendForSignatureButton
                iconOnly
                leadId={contract.lead_id}
                subjectType="contract"
                subjectId={contract.id}
              />
            )}
          <LeadViewContractItemActions
            contract={contract}
            viewOnly={viewOnly}
            isUpdateAllowed={isUpdateAllowed}
            isUploadedFile={isUploadedFile}
            onViewPDF={onViewPDF}
            onDuplicateContract={onDuplicateContract}
            onViewContract={onViewContract}
            onSendToReview={onSendToReview}
            onMarkAsSent={onMarkAsSent}
            onShare={onShare}
          />
        </Flex>
      </Flex>
      {displayReason ? (
        <Flex vertical>
          <Text strong>{isCancelled ? "Cancellation reason" : "Reason"}</Text>
          <Paragraph
            type={"secondary"}
            ellipsis={{ rows: 3, expandable: true, symbol: "more" }}
          >
            {displayReason}
          </Paragraph>
        </Flex>
      ) : null}
    </Flex>
  )
}
